/**
 * @fileoverview PhoneInputRenderer component for the Storyteller web application.
 * Renders a phone number input field with country code formatting and validation.
 * Uses libphonenumber-js for phone number formatting based on country.
 *
 * @example
 * // Basic usage
 * <PhoneInputRenderer
 *   schema={{
 *     type: 'string'
 *   }}
 *   uischema={{
 *     options: {
 *       country: 'US'
 *     }
 *   }}
 *   path="phoneNumber"
 *   handleChange={(path, value) => console.log(path, value)}
 * />
 */


import { ControlProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsControlProps } from '@jsonforms/react'
import { AsYouType, getCountryCallingCode } from 'libphonenumber-js'
import { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import config from '../../../../config'
import css from './PhoneInputRenderer.module.css'


/**
 * @interface PhoneInputProps
 * @description Interface for the PhoneInputRenderer component.
 * @property {string} data - The data for the phone input.
 * @property {string} label - The label for the phone input.
 * @property {boolean} required - Whether the phone input is required.
 * @property {function} handleChange - The function to handle the change of the phone input.
 * @property {string} path - The path of the phone input.
 */
interface PhoneInputProps extends ControlProps {
    data: string
    label: string
    required?: boolean
    handleChange(path: string, value: string): void
    path: string
}

/**
 * @function classNames
 * @description Helper function to join class names with spaces.
 * @param {...string} classes - The class names to join.
 * @returns {string} The joined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function PhoneInputComponent
 * @description Component for rendering a phone input field.
 * @param {PhoneInputProps} props - The props for the PhoneInputComponent.
 * @returns {JSX.Element} The PhoneInputComponent.
 */
function PhoneInputComponent({
    path,
    uischema,
    handleChange,
    errors,
    data: value = '',
}: PhoneInputProps) {
    const { t } = useTranslation()
    const countryCode = uischema.options.country.toUpperCase()

    const formatPhoneNumber = (input: string) => {
        const asYouType = new AsYouType(countryCode)
        const countryCallingCode = getCountryCallingCode(countryCode)

        // If input starts with +, assume it is an international number
        // And pass it directly to the input method
        if (input.startsWith('+')) {
            asYouType.input(input)
        }
        // If input starts with the country calling code
        // Add the '+' prefix and pass it to the input method
        else if (input.startsWith(countryCallingCode)) {
            asYouType.input(`+${input}`)
        }
        // For other cases, manually prepend the '+' and country calling code
        // before using the input method
        else {
            asYouType.input(`+${countryCallingCode}${input}`)
        }

        const phoneNumber = asYouType.getNumber()
        return phoneNumber ? phoneNumber.formatInternational() : input
    }

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value
        if (rawValue.length > 15) return

        const onlyNumbers = rawValue.replace(/[^+0-9]*/g, '')
        handleChange(path, onlyNumbers)
    }

    return (
        <>
            <div className={classNames(css.container, errors.length > 0 && css.error)}>
                <div className={css.flag}>
                    <img
                        width={24}
                        height={22}
                        className={css.logo}
                        src={config.s3.getStaticURL(`${uischema.options.country}.svg`)}
                        alt={`${uischema.options.country} flag`}
                    />
                </div>
                <input
                    type="tel"
                    value={formatPhoneNumber(value)}
                    onChange={handleInputChange}
                    className={css.input}
                    placeholder={t(uischema.options.placeholder)}
                />
            </div>
            {errors.length ? <p className={css.errorMessage}>{errors}</p> : null}
        </>
    )
}

export const PhoneInputTester = rankWith(3, uiTypeIs('Phone'))

export default withJsonFormsControlProps(PhoneInputComponent)
